/**
 * ChatRoom Domain Model
 *
 * Provides type-safe access to chat room data with validation and type guards.
 * Eliminates unsafe type casting by centralizing ChatRoomDto handling.
 */

import type { ChatRoomResponseDto } from "@semo/api-contracts";

export class ChatRoom {
  private constructor(private readonly _data: ChatRoomResponseDto) {}

  /**
   * Get the underlying DTO data
   */
  get data(): ChatRoomResponseDto {
    return this._data;
  }

  /**
   * Factory method to create ChatRoom from DTO
   */
  static fromDto(dto: ChatRoomResponseDto): ChatRoom {
    return new ChatRoom(dto);
  }

  /**
   * Type guard: Check if chat has valid user info
   */
  static hasUserInfo(
    chat: ChatRoomResponseDto,
  ): chat is ChatRoomResponseDto & { userInfo: { name: string } } {
    return !!chat.userInfo?.name;
  }

  /**
   * Type guard: Check if chat has quote request info
   */
  static hasQuoteRequest(
    chat: ChatRoomResponseDto,
  ): chat is ChatRoomResponseDto & { quoteRequest: { projectName: string } } {
    return !!chat.quoteRequest?.projectName;
  }

  /**
   * Type guard: Check if chat has quote response
   */
  static hasQuoteResponse(
    chat: ChatRoomResponseDto,
  ): chat is ChatRoomResponseDto & { quoteResponseId: string } {
    return !!chat.quoteResponseId;
  }

  // Getters
  get id(): string {
    return this._data.id;
  }

  get channelName(): string | undefined {
    return this._data.channelName;
  }

  get events(): unknown {
    return this._data.events;
  }

  get quoteRequestId(): string | undefined {
    return this._data.quoteRequestId;
  }

  get quoteResponseId(): string | undefined {
    return this._data.quoteResponseId;
  }

  get status(): string | undefined {
    return this._data.status;
  }

  get unreadCount(): number {
    return this._data.unreadCount ?? 0;
  }

  get lastMessage(): ChatRoomResponseDto["lastMessage"] {
    return this._data.lastMessage;
  }

  get lastMessageAt(): string | undefined {
    return this._data.lastMessageAt;
  }

  /**
   * Get customer name with fallback
   */
  getCustomerName(): string {
    return ChatRoom.hasUserInfo(this._data)
      ? this._data.userInfo.name
      : "구매자";
  }

  /**
   * Get project name with fallback
   */
  getProjectName(): string {
    return ChatRoom.hasQuoteRequest(this._data)
      ? this._data.quoteRequest.projectName
      : "채팅방";
  }

  /**
   * Check if chat has unread messages
   */
  hasUnreadMessages(): boolean {
    return this.unreadCount > 0;
  }

  /**
   * Check if contract is active
   */
  isContractActive(): boolean {
    return this.status === "contract_active";
  }

  /**
   * Check if quote was sent
   */
  isQuoteSent(): boolean {
    return !!this.quoteResponseId && !this.isContractActive();
  }

  /**
   * Get raw DTO (use sparingly, prefer domain methods)
   */
  toDto(): ChatRoomResponseDto {
    return this._data;
  }

  /**
   * Get user info if available
   */
  getUserInfo(): ChatRoomResponseDto["userInfo"] | undefined {
    return this._data.userInfo;
  }

  /**
   * Get quote request if available
   */
  getQuoteRequest(): ChatRoomResponseDto["quoteRequest"] | undefined {
    return this._data.quoteRequest;
  }
}
